import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
    Chip,
    DialogTitle,
    DialogContent,
    Dialog,
    useTheme,
    useMediaQuery
} from "@mui/material";
import {
    CalendarMonth,
    Inventory2,
    BusinessCenter,
    Person,
    Email,
    Login,
    LocationOn,
    HeadsetMic,
    Logout,
    DoubleArrow,
    ArrowForward,
    InfoOutlined
} from "@mui/icons-material";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import {
    fetchOrganizationById,
    fetchProductsByOrgId,
    fetchServicesbyOrgId,
    fetchSchedulesByServiceId,
    fetchAvailabilitiesByScheduleId,
    createBooking,
    prepareWaitlistBooking,
    createWaitlistBooking
} from "../../redux/actions/apiActions";
import SlotPicker from "../../components/SlotPicker/SlotPicker";
import moment from "moment-timezone";
import { formatPrice } from "../../utils/misc";
import SurveyModal from "../../components/SurveyModal/SurveyModal";
import * as Yup from 'yup';
import { useFormik } from "formik";
import PhoneNumberInput from "../../components/PhoneNumberInput/PhoneNumberInput";
import { confirmGuestPhoneNumberOtp, createGuestUserAccount, setShowLoginForm, userLogout } from "../../redux/actions/authActions";
import { Config } from "../../config/regions";
import SnackbarAlert from "../../components/Alert/SnackbarAlert";
import AuthScreens from "../Auth/AuthScreens";
import OtpInput from "../../components/OtpInput/OtpInput";
import { showAlert } from "../../redux/actions/commonActions";

const QuickBook = () => {
    const { orgRef } = useParams();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const isDarkMode = theme.palette.mode === "dark";

    // -----------------------------------
    // Redux State
    // -----------------------------------
    const orgDetails = useSelector((state) => state.api.orgDetails);
    const orgDetailsLoading = useSelector((state) => state.api.orgDetailsLoading);
    const serviceList = useSelector((state) => state.api.serviceList || []);
    const serviceListLoading = useSelector((state) => state.api.serviceListLoading);
    const productList = useSelector((state) => state.api.productList || []);
    const productListLoading = useSelector((state) => state.api.productListLoading);
    const scheduleList = useSelector((state) => state.api.scheduleList || []);
    const scheduleListLoading = useSelector((state) => state.api.scheduleListLoading);
    const availabilityList = useSelector((state) => state.api.availabilities || []);
    const availabilityListLoading = useSelector((state) => state.api.availabilitiesLoading);
    const userData = useSelector(state => state.auth.userData);
    const showUserFormData = useSelector(state => state.common.showUserFormData);

    // -----------------------------------
    // Local State
    // -----------------------------------
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [availabilities, setAvailabilities] = useState([]);
    const [filteredAvailabilities, setFilteredAvailabilities] = useState([]);
    const [availabilitiesLoading, setAvailabilitiesLoading] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [showAdditionalInfoModal, setShowAdditionalInfoModal] = useState(false);
    const [isBookingForAnotherUser, setIsBookingForAnotherUser] = useState(false);
    const [otpFormData, setOtpFormData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();

    const phoneRegExp = /^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    const userFormSchema = Yup.object({
        customerName: Yup.string().required("Name is required").min(3, "Name should be at least 3 characters"),
        customerPhone: ["IN", "IN_TEST"].includes(Config.value)
            ? Yup.string().required("Phone number is required").matches(phoneRegExp, 'Phone number is not valid')
            : Yup.string().matches(phoneRegExp, 'Phone number is not valid'),
        customerEmail: Yup.string().email("Invalid Email")
    });

    const { values, handleChange, handleBlur, handleSubmit, isSubmitting, errors, touched, setTouched, setFieldValue, setFieldError, resetForm, validateForm } = useFormik({
        initialValues: {
            customerName: userData?.givenName ?? '',
            customerPhone: userData?.telephone ?? '',
            customerEmail: userData?.email ?? '',
            otp: ""
        },
        validationSchema: userFormSchema,
        enableReinitialize: true,
        onSubmit: async values => { },
    });

    useEffect(() => {
        if (orgRef && orgDetails?.orgRef !== orgRef) {
            dispatch(fetchOrganizationById(orgRef));
        }
    }, [orgRef]);

    useEffect(() => {
        if (!orgDetails?.id) return;
        dispatch(fetchServicesbyOrgId(orgDetails.orgRef));
        if (orgDetails?.preferences?.enableProducts) {
            dispatch(fetchProductsByOrgId(orgDetails.id));
        }
    }, [orgDetails]);

    const productOptions = useMemo(() => {
        return productList.map((product) => ({ ...product, label: product.name }));
    }, [productList]);

    const serviceOptions = useMemo(() => {
        if (orgDetails?.preferences?.enableProducts) {
            if (!selectedProduct) return [];
            return (selectedProduct?.services || []).map((service) => ({ ...(serviceList||[]).find(s => s.id === service.serviceId) }));
        }
        return serviceList.map((service) => ({ ...service, label: service.name }));
    }, [serviceList, selectedProduct, orgDetails]);

    useEffect(() => {
        if (orgDetails?.preferences?.enableProducts && productOptions.length === 1 && !selectedProduct) {
            setSelectedProduct(productOptions[0]);
        }
    }, [productOptions]);

    useEffect(() => {
        if (serviceOptions.length === 1 && !selectedService) {
            setSelectedService(serviceOptions[0]);
        }
    }, [serviceOptions]);

    useEffect(() => {
        if (!selectedService?.id) return;
        dispatch(fetchSchedulesByServiceId(selectedService.id));
        setSelectedSchedule(null);
        setSelectedSlot(null);
        setAvailabilities([]);
        setAvailabilitiesLoading(false);
    }, [selectedService]);

    const scheduleOptions = useMemo(() => {
        return scheduleList.map((schedule) => {
            const labelParts = [];
            if (schedule?.providerName) labelParts.push(schedule.providerName);
            if (schedule?.isOnline) labelParts.push("Online");
            if (schedule?.dateLabel) labelParts.push(schedule.dateLabel);
            return { ...schedule, label: labelParts.join(" • ") };
        });
    }, [scheduleList]);

    useEffect(() => {
        if (selectedService?.id && scheduleOptions.length === 1 && !selectedSchedule) {
            setSelectedSchedule(scheduleOptions[0]);
        }
    }, [scheduleOptions]);

    useEffect(() => {
        if (!selectedSchedule?.id) return;
        dispatch(fetchAvailabilitiesByScheduleId(selectedSchedule.id, '?includeCurrentSlot=true'));
        setSelectedSlot(null);
        setAvailabilitiesLoading(true);
    }, [selectedSchedule]);

    useEffect(() => {
        if (!selectedSchedule?.id) return;
        setAvailabilities(availabilityList?.[selectedSchedule.id] || []);
        setSelectedSlot(null);
    }, [availabilityList]);

    useEffect(() => {
        if (!selectedSchedule?.id) return;
        setAvailabilitiesLoading(availabilityListLoading?.[selectedSchedule.id] || false);
    }, [availabilityListLoading]);

    useEffect(() => {
        if (Array.isArray(selectedSlot) && selectedSlot.length) {
            const firstSlotSec = selectedSlot[0]?.startSec;
            const horizonDays = selectedSchedule?.multiBooking?.horizonDays ?? 2;
            const gapMinutes = selectedSchedule?.multiBooking?.gapMinutes ?? 0;
            const maxSlots = selectedSchedule?.multiBooking?.maxSlots ?? selectedSchedule?.multiBooking?.packageLimit ?? Infinity;
            const selectedSlotIds = selectedSlot.map(item => item.id);
            const gapInSec = gapMinutes * 60;

            const horizonEndSec = moment(firstSlotSec * 1000).add(horizonDays, "days").endOf("day").unix();

            let filtered = availabilities
                .filter(item => item.startSec < horizonEndSec)
                .map(item => {
                    const isSelected = selectedSlotIds.includes(item.id);
                    let disabled = false;
                    if (selectedSlot.length >= maxSlots) {
                        disabled = !isSelected;
                    } else {
                        disabled = selectedSlot.some(sel => {
                            const selStart = sel.startSec;
                            return (!isSelected && item.startSec > selStart - gapInSec && item.startSec < selStart + gapInSec);
                        });
                    }
                    return { ...item, disabled };
                });
            setFilteredAvailabilities(filtered);
        } else {
            setFilteredAvailabilities(availabilities);
        }
    }, [availabilities, selectedSlot, selectedSchedule]);

    useEffect(() => {
        if (showUserFormData?.showAlertForSlotTimeConflict && showUserFormData?.alertConflictMessage) {
            dispatch(showAlert('warning', showUserFormData.alertConflictMessage));
        }
    }, [showUserFormData]);

    const onSelectSlot = (slot) => { setSelectedSlot(slot); };

    const onClickContinueBooking = async (slot) => {
        await setTouched({ customerName: true, customerEmail: true, customerPhone: true });
        const validationErrors = await validateForm();
        if (Object.keys(validationErrors)?.length) return;
        onClickSlotConfirm(slot);
    };

    const onJoinQueue = (slot) => {
        setSelectedSlot(prev => prev?.startSec !== slot?.startSec ? slot : null);
        onClickContinueBooking(slot);
    };

    const onClickSlotConfirm = (slot) => {
        const selectedSlot_ = slot ?? selectedSlot;
        const data = { scheduleData: selectedSchedule, selectedSlot: selectedSlot_ };
        if (selectedSchedule?.surveyId) {
            setShowAdditionalInfoModal(true);
        } else {
            proceedToBook(data);
            setSelectedSlot(null);
        }
    };

    const onCompleteAdditionalInfo = (surveyData) => {
        if (surveyData.data) {
            setShowAdditionalInfoModal(false);
            let assesmentData = {
                eventId: null,
                participantId: null,
                questionAnswers: JSON.stringify(surveyData.data),
                status: "COMPLETED",
                surveyId: selectedSchedule.surveyId
            };
            setSelectedSlot(null);
            proceedToBook({ scheduleData: selectedSchedule, selectedSlot, assesmentData });
        }
    };

    const proceedToBook = async (formData) => {
        try {
            let userPayload = {
                email: values.customerEmail,
                givenName: values.customerName,
                phone: values.customerPhone,
                tmpServiceId: formData?.scheduleData?.id,
                userType: "GUEST"
            };
            setIsLoading(true);
            const userDetails = (userData.id && userData.email === values.customerEmail && userData.telephone === values.customerPhone) || (userData.id && isBookingForAnotherUser)
                ? userData
                : await createGuetUser(userPayload);

            if (userDetails.userType === "GUEST" && !userDetails.isPhoneVerified && userDetails.otpSend) {
                setOtpFormData({ userDetails: userDetails, formData: formData });
            } else {
                await completeBookingProcess(formData, userDetails, values);
            }
            setIsLoading(false);
        } catch (e) {
            setIsLoading(false);
            console.error(e);
        }
    };

    const confirmOTP = async () => {
        setIsLoading(true);
        const userDetails = await dispatch(confirmGuestPhoneNumberOtp(otpFormData?.userDetails?.telephone, values.otp));
        if (userDetails?.id) {
            await completeBookingProcess(otpFormData?.formData, userDetails, values);
            setOtpFormData(null);
        }
        setIsLoading(false);
    };

    const completeBookingProcess = async (formData, userDetails, formValues = {}) => {
        if (userDetails?.id) {
            let bookingDetails;
            if (selectedService.mode === "QUEUE") {
                let prepareQueuePayload = {
                    customerId: userDetails.id,
                    startSec: moment(formData?.selectedSlot?.startSec * 1000).startOf('day').unix(),
                    tempServiceId: formData?.scheduleData?.id,
                    toSec: moment(formData?.selectedSlot?.startSec * 1000).endOf('day').unix(),
                };
                const waitListDetails = await prepareQueue(prepareQueuePayload);
                if (waitListDetails?.availabilityId) {
                    let queuePayload = {
                        customerId: waitListDetails?.customerId ?? userDetails?.id,
                        productId: orgDetails?.preferences?.enableProducts && selectedProduct?.id ? selectedProduct?.id : null,
                        duration: formData?.scheduleData?.avgServiceTime,
                        availabilityId: waitListDetails?.availabilityId,
                        startSec: waitListDetails?.startSec,
                        status: "UNSPECIFIED",
                        type: "APPOINTMENT",
                        responseSurveys: formData?.scheduleData?.surveyId && formData?.assesmentData ? formData.assesmentData : {}
                    };
                    bookingDetails = await createQueue(queuePayload);
                }
            } else {
                let bookingPayload = {
                    customerId: userDetails.id,
                    productId: orgDetails?.preferences?.enableProducts && selectedProduct?.id ? selectedProduct?.id : null,
                    duration: formData?.scheduleData?.avgServiceTime,
                    status: "UNSPECIFIED",
                    type: "APPOINTMENT",
                    allowCurrentSlot: true,
                    responseSurveys: formData?.scheduleData?.surveyId && formData?.assesmentData ? formData.assesmentData : {}
                };
                if (Array.isArray(formData?.selectedSlot)) {
                    bookingPayload.availabilityIds = formData.selectedSlot.map(slot => slot.id);
                    bookingPayload.startSec = formData.selectedSlot[0]?.startSec ?? null;
                } else {
                    bookingPayload.availabilityId = formData?.selectedSlot?.id;
                    bookingPayload.startSec = formData?.selectedSlot?.startSec;
                }
                if (isBookingForAnotherUser) {
                    bookingPayload.isBookingForAnotherPerson = true;
                    bookingPayload.actualCustomerDetails = {
                        customerName: formValues?.customerName ?? '',
                        customerPhone: formValues?.customerPhone ?? '',
                        customerEmail: formValues?.customerEmail ?? '',
                    };
                }
                bookingDetails = await createAppointment(bookingPayload);
            }
            if (bookingDetails?.id) {
                navigate(`/event/${bookingDetails?.id}`);
            }
        }
    };

    const createGuetUser = async (payload) => await dispatch(createGuestUserAccount(payload));
    const createAppointment = async (payload) => await dispatch(createBooking(payload));
    const prepareQueue = async (payload) => await dispatch(prepareWaitlistBooking(payload));
    const createQueue = async (payload) => await dispatch(createWaitlistBooking(payload));

    const selectedScheduleData = selectedSchedule;
    const selectedServiceData = selectedService;

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: "background.default", py: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2 }, color: "text.primary" }}>
            <Box sx={{ maxWidth: 1000, mx: "auto" }}>

                {/* Slim Header Banner */}
                <Paper
                    elevation={0}
                    sx={{
                        mb: 2,
                        borderRadius: 3,
                        overflow: "hidden",
                        background: isDarkMode ? theme.palette.background.paper : "#1e293b",
                        backgroundImage: isDarkMode ? theme.palette.background.backgroundImage : "none",
                        color: isDarkMode ? "text.primary" : "white",
                        p: 2,
                        border: "1px solid",
                        borderColor: isDarkMode ? "divider" : "#334155"
                    }}
                >
                    {orgDetailsLoading ? (
                        <CircularProgress size={20} sx={{ color: isDarkMode ? "text.primary" : "white" }} />
                    ) : (
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                                src={orgDetails?.logo?.fileUrl}
                                alt={orgDetails?.name}
                                variant="rounded"
                                sx={{ width: 44, height: 44, bgcolor: "white" }}
                            >
                                {orgDetails?.name?.charAt(0)}
                            </Avatar>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                    {orgDetails?.name || "Book an Appointment"}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.7, color: "inherit" }}>
                                    Please fill out the details below to book your spot.
                                </Typography>
                            </Box>
                        </Stack>
                    )}
                </Paper>

                {/* Main Content Grid */}
                <Grid container spacing={2}>

                    {/* Left Interaction Form Column */}
                    <Grid item xs={12} md={selectedSlot ? 7 : 12} lg={selectedSlot ? 7.5 : 12} sx={{ transition: "all 0.3s ease" }}>
                        <Stack spacing={2}>

                            {/* Step 1: Your Information */}
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: "background.paper", borderColor: "divider" }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                                    <Typography variant="body1" fontWeight={700}>
                                        1. Your Information
                                    </Typography>
                                    {userData?.id ? (
                                        <Button size="small" variant="text" color="error" startIcon={<Logout />} onClick={() => { resetForm(); dispatch(userLogout()); }} sx={{ fontSize: '0.75rem', p: 0 }}>
                                            Log Out
                                        </Button>
                                    ) : (
                                        <Button size="small" variant="text" startIcon={<Login />} onClick={() => dispatch(setShowLoginForm(true))} sx={{ fontSize: '0.75rem', p: 0 }}>
                                            Already a customer? Login
                                        </Button>
                                    )}
                                </Stack>

                                <Grid container spacing={1.5}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Name"
                                            placeholder="Enter your full name"
                                            onChange={handleChange}
                                            value={values.customerName}
                                            name="customerName"
                                            helperText={touched?.customerName && errors?.customerName}
                                            error={!!(touched?.customerName && errors?.customerName)}
                                            InputProps={{ startAdornment: <Person sx={{ mr: 1, color: "text.disabled", fontSize: 18 }} /> }}
                                            disabled={!!userData?.id && !isBookingForAnotherUser}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <PhoneNumberInput
                                            label="Phone Number"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            name="customerPhone"
                                            onChange={(phone) => setFieldValue("customerPhone", phone)}
                                            value={values.customerPhone}
                                            error={!!(touched?.customerPhone && errors?.customerPhone)}
                                            helperText={touched?.customerPhone && errors?.customerPhone ? errors?.customerPhone : ''}
                                            readOnly={!!userData?.id}
                                            disabled={!!userData?.id}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Email (Optional)"
                                            placeholder="Enter your email address"
                                            name="customerEmail"
                                            onChange={handleChange}
                                            value={values.customerEmail}
                                            error={!!(touched?.customerEmail && errors?.customerEmail)}
                                            helperText={touched?.customerEmail && errors?.customerEmail}
                                            InputProps={{ startAdornment: <Email sx={{ mr: 1, color: "text.disabled", fontSize: 18 }} /> }}
                                            disabled={!!userData?.id && !isBookingForAnotherUser}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Step 2: Service Selection Configuration */}
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: "background.paper", borderColor: "divider" }}>
                                <Typography variant="body1" fontWeight={700} mb={2}>
                                    2. Select Option
                                </Typography>

                                <Stack spacing={2}>
                                    {orgDetails?.preferences?.enableProducts && productOptions.length > 1 && (
                                        <Autocomplete
                                            value={selectedProduct}
                                            options={productOptions}
                                            loading={productListLoading}
                                            size="small"
                                            onChange={(e, value) => {
                                                setSelectedProduct(value);
                                                setSelectedService(null);
                                                setSelectedSchedule(null);
                                                setSelectedSlot(null);
                                                setAvailabilities([]);
                                                setAvailabilitiesLoading(false);
                                            }}
                                            getOptionLabel={(option) => option?.label || ""}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Product"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <Inventory2 sx={{ mr: 1, color: "text.disabled", fontSize: 18 }} />
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                        endAdornment: (
                                                            <>
                                                                {productListLoading && <CircularProgress size={14} />}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    )}

                                    {(!orgDetails?.preferences?.enableProducts || selectedProduct) && (
                                        <Autocomplete
                                            value={selectedService}
                                            options={serviceOptions}
                                            loading={serviceListLoading}
                                            size="small"
                                            onChange={(e, value) => setSelectedService(value)}
                                            getOptionLabel={(option) => option?.name || ""}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Service"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <BusinessCenter sx={{ mr: 1, color: "text.disabled", fontSize: 18 }} />
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                        endAdornment: (
                                                            <>
                                                                {serviceListLoading && <CircularProgress size={14} />}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    )}

                                    {selectedService && (
                                        <Autocomplete
                                            value={selectedSchedule}
                                            options={scheduleOptions}
                                            loading={scheduleListLoading}
                                            size="small"
                                            onChange={(e, value) => setSelectedSchedule(value)}
                                            getOptionLabel={(option) => option?.label || ""}
                                            renderOption={(props, option) => (
                                                <Box component="li" {...props} sx={{ py: 1, px: 1.5, fontSize: '0.875rem' }}>
                                                    <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {option.label}
                                                            </Typography>
                                                            {option.isOnline ? (
                                                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary" }}>
                                                                    <HeadsetMic sx={{ fontSize: 14 }} />
                                                                    <Typography variant="caption">Online {option.onlineMeetingProvider ?` (${option.onlineMeetingProvider})` : ''}</Typography>
                                                                </Stack>
                                                            ) : (
                                                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary" }}>
                                                                    <LocationOn sx={{ fontSize: 14 }} />
                                                                    <Typography variant="caption">
                                                                        {option.geoLocation?.name || option.geoLocation?.fullAddress || "In-person"}
                                                                    </Typography>
                                                                </Stack>
                                                            )}
                                                        </Box>
                                                        {option?.payment?.amount > 0 && (
                                                            <Chip
                                                                label={formatPrice(option.payment.amount, option.payment.currency || "")}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Stack>
                                                </Box>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Service Options"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <CalendarMonth sx={{ mr: 1, color: "text.disabled", fontSize: 18 }} />
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                        endAdornment: (
                                                            <>
                                                                {scheduleListLoading && <CircularProgress size={14} />}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                </Stack>
                            </Paper>

                            {/* Step 3: Day and Time picker element */}
                            {selectedSchedule && (
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: "background.paper", borderColor: "divider" }}>
                                    <Typography variant="body1" fontWeight={700} mb={1.5}>
                                        3. Choose Time
                                    </Typography>

                                    {availabilitiesLoading ? (
                                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                            <CircularProgress size={28} />
                                        </Box>
                                    ) : filteredAvailabilities.length > 0 ? (
                                        <SlotPicker
                                            availabilities={filteredAvailabilities}
                                            onSelectSlot={onSelectSlot}
                                            onJoinQueue={onJoinQueue}
                                            selectedSlotIds={Array.isArray(selectedSlot) ? selectedSlot.map(item => item.id) : selectedSlot?.id}
                                            timezoneId={selectedScheduleData?.timezoneId}
                                            mode={selectedServiceData?.mode}
                                        />
                                    ) : (
                                        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2, py: 0.5, fontSize: '0.875rem' }}>
                                            No available times found for your selection.
                                        </Alert>
                                    )}
                                </Paper>
                            )}

                        </Stack>
                    </Grid>

                    {/* Right Sticky Action/Summary Column (Only displays when slot chosen to optimize whitespace) */}
                    {selectedSlot && (
                        <Grid item xs={12} md={5} lg={4.5}>
                            <Box sx={{ position: isDesktop ? "sticky" : "static", top: 12 }}>
                                <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: "background.paper", borderColor: "divider", boxShadow: "none" }}>
                                    <Box sx={{ p: 2, bgcolor: isDarkMode ? "action.hover" : "#f8fafc", borderBottom: "1px solid", borderColor: "divider" }}>
                                        <Typography variant="subtitle2" fontWeight={700} >
                                            Your Selection
                                        </Typography>
                                    </Box>
                                    <CardContent sx={{ p: 2, pb: "16px !important" }}>
                                        <Stack spacing={1.5}>
                                            {selectedService && (
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block">Service</Typography>
                                                    <Typography variant="body2" fontWeight={600}>{selectedService.label}</Typography>
                                                </Box>
                                            )}

                                            <Box>
                                                <Typography variant="caption" color="text.secondary" display="block">Time Slot</Typography>
                                                <Typography variant="body2" fontWeight={600} color="primary.main">
                                                    {Array.isArray(selectedSlot)
                                                        ? `${selectedSlot.length} slots chosen`
                                                        : moment(selectedSlot?.startSec * 1000).tz(selectedScheduleData?.timezoneId || moment.tz.guess()).format("MMM Do, hh:mm A")}
                                                </Typography>
                                            </Box>

                                            {selectedSchedule?.payment?.amount > 0 && (
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block">Price</Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {formatPrice(selectedSchedule.payment.amount, selectedSchedule.payment.currency || "")}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {selectedService?.mode !== "QUEUE" && (
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    size="medium"
                                                    endIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
                                                    sx={{ height: 42, borderRadius: 2, fontWeight: 700, mt: 1 }}
                                                    onClick={() => onClickContinueBooking()}
                                                    disabled={isLoading}
                                                >
                                                    Confirm Booking
                                                </Button>
                                            )}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>

            {/* Verification Dialog Framework */}
            {selectedSchedule?.surveyId && (
                <SurveyModal
                    show={showAdditionalInfoModal}
                    onClose={() => setShowAdditionalInfoModal(false)}
                    title="Additional Information"
                    surveyId={selectedSchedule?.surveyId}
                    completeText="CONFIRM BOOKING"
                    onComplete={onCompleteAdditionalInfo}
                />
            )}

            <Dialog
                open={!!otpFormData}
                onClose={() => { }}
                PaperProps={{
                    sx: {
                        width: '100%',
                        m: { xs: 0, sm: 2 },
                        borderRadius: { xs: "16px 16px 0 0", sm: 3 },
                        alignSelf: { xs: 'flex-end', sm: 'center' },
                        maxWidth: 400,
                        bgcolor: "background.paper",
                        backgroundImage: "none"
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, p: 2, pb: 0, fontSize: '1.1rem' }}>Enter Verification Code</DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            We sent a secure verification code to <strong>{otpFormData?.userDetails?.telephone ?? ''}</strong>.
                        </Typography>
                        <Box sx={{ my: 2 }}>
                            <OtpInput
                                value={values.otp}
                                onChange={(otp) => setFieldValue("otp", otp)}
                            />
                        </Box>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={confirmOTP}
                            disabled={isLoading || !values.otp || values.otp.length < 6}
                            endIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <DoubleArrow />}
                            sx={{ borderRadius: 2, mt: 2, height: 44 }}
                        >
                            Verify & Book Now
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <AuthScreens />
            <SnackbarAlert />
        </Box>
    );
};

export default QuickBook;